// ============================================================
// COMBAT RESOLUTION — Step C of each Tick (SRS §3.3.4)
// ============================================================
//
// Type-group attack order (from BattleDirector.ATTACK_ORDER):
//   Mage(0) → Archer(1) → Cavalry(2) → Infantry(3)
//
// Within a group all attackers SIMULTANEOUSLY select targets based
// on the state at group-start, then damage is accumulated per
// target and applied in one batch — this prevents any attacker in
// the same group from killing a same-type peer pre-emptively.
//
// Between groups, damage from the previous group is visible: a Mage
// dead in group 0 cannot attack or be targeted by group 1.
//
// The emitted `CombatGroup[]` is the server analogue of
// BattleDirector's `groupApplies` — each group carries its own
// unit/base damage list so the client can apply damage exactly when
// the group's animation begins.

import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import { UCFG } from '../constants.ts';
import { cd } from '../utils.ts';
import { applyDamageToUnit, applyDamageToBase } from './damage.ts';
import type { TerrainGrid } from './terrain.ts';
import type { AttackRecord, CombatGroup } from './types.ts';

export const COMBAT_TYPE_ORDER = ['Mage', 'Archer', 'Cavalry', 'Infantry'] as const;

/**
 * Resolve Step C combat. `terrain` is accepted (and forwarded for future
 * terrain-aware combat mechanics) but currently unused — the original
 * Archer grass-cover discount was removed (Session 12 follow-up):
 * archers no longer take a damage penalty against units standing on
 * grass / burningGrass. Targets on grass are hit at full damage like
 * any other tile.
 */
export function resolveCombat(
  units: BattleUnit[],
  bases: Base[],
  _terrain?: TerrainGrid,
): CombatGroup[] {
  const groups: CombatGroup[] = [];

  for (const typeName of COMBAT_TYPE_ORDER) {
    const groupUnits = units.filter((u) => !u.dead && u.type === typeName);
    if (groupUnits.length === 0) continue;

    // Phase 1: targets are selected against the state at group-start.
    const attacks: AttackRecord[] = [];
    for (const u of groupUnits) {
      const target = _findBestTarget(u, units, bases);
      if (!target) continue;
      // Session 12 follow-up — Archer grass cover discount removed.
      // Archers now deal full `u.atk` to grass-standing targets.
      // v2 — 在此就 `Math.ceil`（原設計 damage = ceil(兵力 × atk)）：atk 砍到 1/3 後
      // `u.atk` 幾乎都是小數（10.2 / 5.95…），若不取整，飄字顯示小數、且與實際扣兵
      // （applyDamageToUnit 內部 ceil）對不上。在源頭取整 → 顯示=扣兵、全整數（含基地）。
      const dmg = Math.ceil(u.atk);
      attacks.push({
        attackerId: u.id,
        attackerPos: { ...u.pos },
        targetUnitId: target.unit ? target.unit.id : null,
        targetUnitPos: target.unit ? { ...target.unit.pos } : null,
        targetBaseTeam: target.base ? target.base.team : null,
        targetBasePos: target.base ? _baseCenter(target.base.tiles) : null,
        dmg,
        targetDied: false,
        baseDied: false,
      });
    }

    // Phase 2: damage is accumulated per target, then applied atomically.
    const unitDmgAccum = new Map<string, number>();
    const baseDmgAccum = new Map<number, number>();
    for (const a of attacks) {
      if (a.targetUnitId) {
        unitDmgAccum.set(a.targetUnitId, (unitDmgAccum.get(a.targetUnitId) || 0) + a.dmg);
      }
      if (a.targetBaseTeam !== null) {
        baseDmgAccum.set(a.targetBaseTeam, (baseDmgAccum.get(a.targetBaseTeam) || 0) + a.dmg);
      }
    }

    const unitDamages: CombatGroup['unitDamages'] = [];
    for (const [uid, dmg] of unitDmgAccum) {
      const target = units.find((u) => u.id === uid);
      if (!target || target.dead) continue;
      // Session 7 — apply Shield Wall physical reduction once per
      // accumulated batch (so percentage rounding happens on the total,
      // not per individual attacker — keeps the maths intuitive).
      const reduce = target.tickDefPhys || 0;
      const finalDmg = reduce > 0 ? Math.round(dmg * (1 - reduce)) : dmg;
      const delta = applyDamageToUnit(target, finalDmg);
      unitDamages.push({
        unitId: uid,
        dmg: finalDmg,
        died: delta.died,
        totalSoldiers: delta.totalSoldiers,
      });
    }

    const baseDamages: CombatGroup['baseDamages'] = [];
    for (const [team, dmg] of baseDmgAccum) {
      const base = bases.find((b) => b.team === team);
      if (!base || base.destroyed) continue;
      const delta = applyDamageToBase(base, dmg);
      baseDamages.push({
        team,
        dmg,
        destroyed: delta.destroyed,
        hp: delta.hp,
      });
    }

    // Phase 3: back-fill `targetDied` / `baseDied` flags.
    // Session 12 follow-up #3 — aggro-target write removed. Units no
    // longer remember their last attacker; movement.ts goes straight
    // to enemy-base routing once the planned path is done.
    for (const a of attacks) {
      if (a.targetUnitId) {
        const t = units.find((u) => u.id === a.targetUnitId);
        if (t) a.targetDied = t.dead;
      }
      if (a.targetBaseTeam !== null) {
        const b = bases.find((bb) => bb.team === a.targetBaseTeam);
        if (b) a.baseDied = b.destroyed;
      }
    }

    groups.push({ groupType: typeName, attacks, unitDamages, baseDamages });
  }

  return groups;
}

// ---------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------

interface Targetable {
  unit?: BattleUnit;
  base?: Base;
  pos: Pos;
  hp: number;
}

function _findBestTarget(u: BattleUnit, units: BattleUnit[], bases: Base[]): Targetable | null {
  const cfg = UCFG[u.type];
  const candidates: Targetable[] = [];

  for (const e of units) {
    if (e.dead || e.team === u.team) continue;
    if (cd(u.pos, e.pos) <= cfg.range) {
      candidates.push({ unit: e, pos: e.pos, hp: e.hp });
    }
  }
  for (const b of bases) {
    if (b.destroyed || b.team === u.team) continue;
    for (const t of b.tiles) {
      if (cd(u.pos, t) <= cfg.range) {
        candidates.push({ base: b, pos: t, hp: b.hp });
        break;
      }
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.hp - b.hp);
  return candidates[0];
}

function _baseCenter(tiles: Pos[]): Pos {
  return {
    x: tiles.reduce((s, t) => s + t.x, 0) / tiles.length,
    y: tiles.reduce((s, t) => s + t.y, 0) / tiles.length,
  };
}
